import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';

// ═══════════════════════════════════════════════════════════════════════════
// HUB Profile Upload Hook
// Supports: solar profile (12×24) and consumption profile (Piekvermogen format)
// Includes automatic kW/kWh unit detection
// ═══════════════════════════════════════════════════════════════════════════

export interface ProfileStats {
  rows: number;
  dateStart: string;
  dateEnd: string;
  /** Detected unit from the source file */
  detectedUnit?: 'kW' | 'kWh';
}

export interface UploadedProfiles {
  /** Custom 12×24 solar profile (kW values per month×hour) — overrides SP */
  solarProfile: number[][] | null;
  /** Solar upload stats */
  solarStats: ProfileStats | null;
  /** Custom 24-hour consumption shape (normalized) — overrides CHP */
  consumptionProfile: number[] | null;
  /** Annual demand calculated from uploaded data (MWh) */
  consumptionDemandMwh: number | null;
  /** Upload stats for display */
  consumptionStats: ProfileStats | null;
  /** Monthly seasonal factors derived from uploaded consumption data (12 values, Jan-Dec) — overrides CSF */
  consumptionSeasonalFactors: number[] | null;
  /** Custom 24-hour grid profile shape (normalized) — overrides grid import pattern */
  gridProfile: number[] | null;
  /**
   * Piek uit het geuploade gridprofiel, in kW. Hieruit wordt de netaansluiting
   * (gMax) afgeleid zodra gridMode op 'upload' staat — dat is wat de knop al
   * beloofde ("Verbruiksprofiel importeren (Piekvermogen)") maar tot
   * 8 september 2026 niet deed.
   */
  gridPeakKw: number | null;
  /** Grid annual MWh from uploaded data */
  gridDemandMwh: number | null;
  /** Grid upload stats */
  gridStats: ProfileStats | null;
}

interface QuarterData {
  timestamp: string;
  kw: number;
}

// ─── Fuzzy column header matching ───
function fuzzyMatch(header: string, patterns: string[]): boolean {
  const normalized = header.toLowerCase().trim();
  return patterns.some(p => normalized.includes(p.toLowerCase()));
}

// ─── Detect if column header indicates kWh instead of kW ───
function detectUnit(header: string): 'kW' | 'kWh' {
  const h = header.toLowerCase().trim();
  // Match kwh, kw·h, kw-h, wh but NOT just kw
  if (/kwh|kw[\.\-·]h|wh\b/.test(h)) return 'kWh';
  return 'kW';
}

// ─── Parse CSV with Piekvermogen format ───
// ─── Detect delimiter from header line ───
function detectDelimiter(headerLine: string): string {
  if (headerLine.includes(';')) return ';';
  if (headerLine.includes('\t')) return '\t';
  return ',';
}

// ─── Strip UTF-8 BOM ───
function stripBOM(text: string): string {
  return text.replace(/^\uFEFF/, '');
}

// ─── Parse CSV with Piekvermogen format ───
function parseCSVRows(rawText: string): { rows: QuarterData[]; detectedUnit: 'kW' | 'kWh' } {
  const text = stripBOM(rawText);
  const lines = text.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) throw new Error('Bestand bevat geen data');

  const delimiter = detectDelimiter(lines[0]);
  const headers = lines[0].split(delimiter).map(h => h.trim().replace(/^["']|["']$/g, ''));
  const timePatterns = ['tijd', 'time', 'timestamp', 'date', 'datum', 'datetime', 'van'];
  const loadPatterns = ['piek', 'load', 'kw', 'power', 'vermogen', 'kwartier', 'piekbelasting', 'verbruik', 'consumption', 'demand'];

  const timeColIndex = headers.findIndex(h => fuzzyMatch(h, timePatterns));
  const loadColIndex = headers.findIndex(h => fuzzyMatch(h, loadPatterns));

  if (timeColIndex === -1 || loadColIndex === -1) {
    throw new Error('Vereiste kolommen (tijd + kW) niet gevonden. Zorg voor kolommen met "tijd/datum" en "kW/vermogen" in de header.');
  }

  const detectedUnit = detectUnit(headers[loadColIndex]);

  const rows: QuarterData[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(delimiter).map(v => v.trim().replace(/^["']|["']$/g, ''));
    if (values.length <= Math.max(timeColIndex, loadColIndex)) continue;

    const timeStr = values[timeColIndex];
    const loadStr = values[loadColIndex].replace(',', '.');
    if (!timeStr || !loadStr) continue;

    const timestamp = parseTimestamp(timeStr);
    if (!timestamp) continue;

    const kw = parseFloat(loadStr);
    if (isNaN(kw)) continue;
    rows.push({ timestamp, kw });
  }

  return { rows, detectedUnit };
}

// ─── Parse Excel with Piekvermogen format ───
async function parseExcelRows(buffer: ArrayBuffer): Promise<{ rows: QuarterData[]; detectedUnit: 'kW' | 'kWh' }> {
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, raw: false, dateNF: 'yyyy-mm-dd hh:mm:ss' });

  if (jsonData.length < 2) throw new Error('Bestand bevat geen data');

  const headers = (jsonData[0] as any[]).map(h => String(h || '').trim());
  const timePatterns = ['tijd', 'time', 'timestamp', 'date', 'datum', 'datetime', 'van'];
  const loadPatterns = ['piek', 'load', 'kw', 'power', 'vermogen', 'kwartier', 'piekbelasting', 'verbruik', 'consumption', 'demand'];

  const timeColIndex = headers.findIndex(h => fuzzyMatch(h, timePatterns));
  const loadColIndex = headers.findIndex(h => fuzzyMatch(h, loadPatterns));

  if (timeColIndex === -1 || loadColIndex === -1) {
    throw new Error('Vereiste kolommen (tijd + kW) niet gevonden. Zorg voor kolommen met "tijd/datum" en "kW/vermogen" in de header.');
  }

  const detectedUnit = detectUnit(headers[loadColIndex]);

  const rows: QuarterData[] = [];
  for (let i = 1; i < jsonData.length; i++) {
    const row = jsonData[i] as any[];
    if (!row || row.length <= Math.max(timeColIndex, loadColIndex)) continue;

    const timeStr = String(row[timeColIndex] || '').trim();
    const loadStr = String(row[loadColIndex] || '').trim().replace(',', '.');
    if (!timeStr || !loadStr) continue;

    const timestamp = parseTimestamp(timeStr);
    if (!timestamp) continue;

    const kw = parseFloat(loadStr);
    if (isNaN(kw)) continue;
    rows.push({ timestamp, kw });
  }

  return { rows, detectedUnit };
}

// ─── Timestamp parsing (handles Dutch formats) ───
function parseTimestamp(timeStr: string): string | null {
  let timestamp = timeStr;

  // Dutch date format: "1-1-2024 00:00:01"
  if (/^\d{1,2}-\d{1,2}-\d{4}/.test(timeStr)) {
    const parts = timeStr.split(' ');
    const dateParts = parts[0].split('-');
    const day = dateParts[0].padStart(2, '0');
    const month = dateParts[1].padStart(2, '0');
    const year = dateParts[2];
    const time = parts[1] || '00:00:00';
    timestamp = `${year}-${month}-${day} ${time}`;
  }
  // Time-only
  else if (/^\d{1,2}[:\-]\d{2}/.test(timeStr) && !/^\d{4}/.test(timeStr)) {
    timestamp = `2024-01-01 ${timeStr}`;
  }
  // Other
  else if (!/\d{4}-\d{2}-\d{2}/.test(timestamp)) {
    const parsed = new Date(timeStr);
    if (!isNaN(parsed.getTime())) {
      timestamp = parsed.toISOString().slice(0, 19).replace('T', ' ');
    } else {
      return null;
    }
  }

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(timestamp)) {
    timestamp += ':00';
  }

  return timestamp;
}

// ─── Convert quarter-hourly data to 24h shape + annual demand + seasonal factors ───
/**
 * Geëxporteerd om te kunnen toetsen: de kWh->kW-omrekening en de piek erin zijn
 * precies het soort rekenwerk dat stil fout kan gaan. Zie
 * tests/hub/gridpiek.node.test.mjs.
 */
export function processQuarterData(rows: QuarterData[], detectedUnit: 'kW' | 'kWh'): {
  hourlyShape: number[];
  annualMwh: number;
  /** Hoogste kwartierwaarde in kW — de piek waar de netaansluiting op moet passen. */
  peakKw: number;
  stats: ProfileStats;
  seasonalFactors: number[];
} {
  rows.sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  // Detect interval (15-min or hourly)
  let intervalMinutes = 15;
  if (rows.length >= 2) {
    const t1 = new Date(rows[0].timestamp).getTime();
    const t2 = new Date(rows[1].timestamp).getTime();
    const diff = (t2 - t1) / 60000;
    if (diff >= 50 && diff <= 70) intervalMinutes = 60;
  }

  const intervalHours = intervalMinutes / 60;

  // If data is in kWh, convert to average kW for the interval
  // kW = kWh / interval_hours
  if (detectedUnit === 'kWh') {
    for (const row of rows) {
      row.kw = row.kw / intervalHours;
    }
  }

  // De piek, in kW. Pas hier te berekenen: hierboven is row.kw zo nodig van kWh
  // naar kW omgerekend, dus vanaf dit punt staat er overal hetzelfde in.
  // Dit is de hoogste losse meting, niet een uurgemiddelde — een aansluiting
  // moet de piek aankunnen, niet het gemiddelde eromheen.
  const peakKw = rows.reduce((max, row) => (row.kw > max ? row.kw : max), 0);

  // Aggregate to hourly buckets
  const hourlyTotals = new Array(24).fill(0);
  const hourlyCounts = new Array(24).fill(0);
  let totalKwh = 0;

  for (const row of rows) {
    const date = new Date(row.timestamp);
    const hour = date.getHours();
    hourlyTotals[hour] += row.kw;
    hourlyCounts[hour]++;
    // kWh = kW × interval_hours (now always correct since we normalized to kW)
    totalKwh += row.kw * intervalHours;
  }

  // Normalize to relative shape
  const hourlyAvg = hourlyTotals.map((sum, i) => hourlyCounts[i] > 0 ? sum / hourlyCounts[i] : 0);
  const avgOfAvg = hourlyAvg.reduce((a, b) => a + b, 0) / 24;
  const hourlyShape = avgOfAvg > 0 ? hourlyAvg.map(v => v / avgOfAvg) : new Array(24).fill(1);

  // Estimate annual MWh
  const dateStart = rows[0].timestamp;
  const dateEnd = rows[rows.length - 1].timestamp;
  const spanMs = new Date(dateEnd).getTime() - new Date(dateStart).getTime();
  const spanDays = Math.max(1, spanMs / 86400000);
  const annualMwh = (totalKwh / spanDays) * 365.25 / 1000;

  // Derive monthly seasonal factors (CSF) from uploaded data
  const monthlyKwh: Record<number, number> = {};
  const monthlyDayCount: Record<number, Set<string>> = {};

  for (const row of rows) {
    const date = new Date(row.timestamp);
    const month = date.getMonth(); // 0-11
    const dateKey = date.toISOString().slice(0, 10);

    if (!monthlyKwh[month]) monthlyKwh[month] = 0;
    if (!monthlyDayCount[month]) monthlyDayCount[month] = new Set();

    // row.kw is already normalized to kW (kWh conversion done above)
    monthlyKwh[month] += row.kw * intervalHours; // kW × interval_hours = kWh
    monthlyDayCount[month].add(dateKey);
  }

  const monthlyAvgDaily = Array.from({ length: 12 }, (_, m) => {
    const totalKwh = monthlyKwh[m] || 0;
    const days = monthlyDayCount[m]?.size || 1;
    return totalKwh / days;
  });

  const nonZeroMonths = monthlyAvgDaily.filter(v => v > 0);
  const overallAvg = nonZeroMonths.length > 0
    ? nonZeroMonths.reduce((a, b) => a + b, 0) / nonZeroMonths.length
    : 1;

  const seasonalFactors = monthlyAvgDaily.map(v =>
    overallAvg > 0 && v > 0 ? v / overallAvg : 1
  );

  return {
    hourlyShape,
    annualMwh,
    peakKw,
    stats: {
      rows: rows.length,
      dateStart: dateStart.slice(0, 10),
      dateEnd: dateEnd.slice(0, 10),
      detectedUnit,
    },
    seasonalFactors,
  };
}

// ─── Parse solar profile (12×24 matrix) ───
function parseSolarFile(rows: string[][]): number[][] {
  const dataRows = rows[0]?.some(c => /[a-zA-Z]/.test(c) && isNaN(parseFloat(c))) ? rows.slice(1) : rows;
  if (dataRows.length >= 12) {
    const matrix: number[][] = [];
    for (let m = 0; m < 12; m++) {
      const row = dataRows[m];
      const monthVals: number[] = [];
      for (let h = 0; h < 24 && h < row.length; h++) {
        const v = parseFloat(row[h]?.replace(',', '.'));
        monthVals.push(isNaN(v) ? 0 : v);
      }
      while (monthVals.length < 24) monthVals.push(0);
      matrix.push(monthVals);
    }
    return matrix;
  }
  throw new Error('Verwacht minimaal 12 rijen (maanden) × 24 kolommen (uren).');
}

// ═══════════════════════════════════════════════════════════════════════════
// Hook
// ═══════════════════════════════════════════════════════════════════════════
export function useHubProfileUpload() {
  const [profiles, setProfiles] = useState<UploadedProfiles>({
    solarProfile: null,
    solarStats: null,
    consumptionProfile: null,
    consumptionDemandMwh: null,
    consumptionStats: null,
    consumptionSeasonalFactors: null,
    gridProfile: null,
    gridPeakKw: null,
    gridDemandMwh: null,
    gridStats: null,
  });
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  // ─── Restore profiles from saved sim_params ───
  const restoreProfiles = useCallback((saved: Partial<UploadedProfiles>) => {
    setProfiles(prev => ({
      ...prev,
      solarProfile: saved.solarProfile ?? prev.solarProfile,
      solarStats: saved.solarStats ?? prev.solarStats,
      consumptionProfile: saved.consumptionProfile ?? prev.consumptionProfile,
      consumptionDemandMwh: saved.consumptionDemandMwh ?? prev.consumptionDemandMwh,
      consumptionStats: saved.consumptionStats ?? prev.consumptionStats,
      consumptionSeasonalFactors: saved.consumptionSeasonalFactors ?? prev.consumptionSeasonalFactors,
      gridProfile: saved.gridProfile ?? prev.gridProfile,
      gridPeakKw: saved.gridPeakKw ?? prev.gridPeakKw,
      gridDemandMwh: saved.gridDemandMwh ?? prev.gridDemandMwh,
      gridStats: saved.gridStats ?? prev.gridStats,
    }));
  }, []);

  const handleSolarUpload = useCallback(async (file: File) => {
    setErrors(prev => ({ ...prev, solar: null }));
    setLoading(prev => ({ ...prev, solar: true }));
    try {
      const ext = file.name.split('.').pop()?.toLowerCase();
      let rawRows: string[][];
      if (ext === 'xlsx' || ext === 'xls') {
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        rawRows = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, raw: false }).map(r => r.map(String));
      } else {
        const text = await file.text();
        rawRows = text.trim().split('\n').map(l => l.trim()).filter(Boolean).map(l => l.split(/[;,\t]/));
      }
      const matrix = parseSolarFile(rawRows);
      const nonZero = matrix.flat().filter(v => v > 0).length;
      setProfiles(prev => ({
        ...prev,
        solarProfile: matrix,
        solarStats: { rows: 12, dateStart: `${nonZero} waarden > 0`, dateEnd: '12×24 matrix' },
      }));
    } catch (err: any) {
      setErrors(prev => ({ ...prev, solar: err.message || 'Fout bij verwerken' }));
    } finally {
      setLoading(prev => ({ ...prev, solar: false }));
    }
  }, []);

  const handleGridUpload = useCallback(async (file: File) => {
    setErrors(prev => ({ ...prev, grid: null }));
    setLoading(prev => ({ ...prev, grid: true }));
    try {
      const ext = file.name.split('.').pop()?.toLowerCase();
      let rows: QuarterData[];
      let detectedUnit: 'kW' | 'kWh';

      if (ext === 'csv' || ext === 'txt') {
        const text = await file.text();
        const result = parseCSVRows(text);
        rows = result.rows;
        detectedUnit = result.detectedUnit;
      } else if (ext === 'xlsx' || ext === 'xls') {
        const buffer = await file.arrayBuffer();
        const result = await parseExcelRows(buffer);
        rows = result.rows;
        detectedUnit = result.detectedUnit;
      } else {
        throw new Error('Ongeldig bestandstype. Upload .csv, .xlsx of .xls');
      }

      if (rows.length < 24) {
        throw new Error(`Te weinig data: ${rows.length} rijen gevonden. Minimaal 24 rijen (1 dag) nodig.`);
      }

      const { hourlyShape, annualMwh, stats, peakKw } = processQuarterData(rows, detectedUnit);

      setProfiles(prev => ({
        ...prev,
        gridProfile: hourlyShape,
        gridPeakKw: peakKw,
        gridDemandMwh: annualMwh,
        gridStats: stats,
      }));
    } catch (err: any) {
      setErrors(prev => ({ ...prev, grid: err.message || 'Fout bij verwerken' }));
    } finally {
      setLoading(prev => ({ ...prev, grid: false }));
    }
  }, []);

  const handleConsumptionUpload = useCallback(async (file: File) => {
    setErrors(prev => ({ ...prev, consumption: null }));
    setLoading(prev => ({ ...prev, consumption: true }));
    try {
      const ext = file.name.split('.').pop()?.toLowerCase();
      let rows: QuarterData[];
      let detectedUnit: 'kW' | 'kWh';

      if (ext === 'csv' || ext === 'txt') {
        const text = await file.text();
        const result = parseCSVRows(text);
        rows = result.rows;
        detectedUnit = result.detectedUnit;
      } else if (ext === 'xlsx' || ext === 'xls') {
        const buffer = await file.arrayBuffer();
        const result = await parseExcelRows(buffer);
        rows = result.rows;
        detectedUnit = result.detectedUnit;
      } else {
        throw new Error('Ongeldig bestandstype. Upload .csv, .xlsx of .xls');
      }

      if (rows.length < 24) {
        throw new Error(`Te weinig data: ${rows.length} rijen gevonden. Minimaal 24 rijen (1 dag) nodig.`);
      }

      const { hourlyShape, annualMwh, stats, seasonalFactors } = processQuarterData(rows, detectedUnit);

      setProfiles(prev => ({
        ...prev,
        consumptionProfile: hourlyShape,
        consumptionDemandMwh: annualMwh,
        consumptionStats: stats,
        consumptionSeasonalFactors: seasonalFactors,
      }));
    } catch (err: any) {
      setErrors(prev => ({ ...prev, consumption: err.message || 'Fout bij verwerken' }));
    } finally {
      setLoading(prev => ({ ...prev, consumption: false }));
    }
  }, []);

  const clearSolar = useCallback(() => {
    setProfiles(prev => ({ ...prev, solarProfile: null, solarStats: null }));
    setErrors(prev => ({ ...prev, solar: null }));
  }, []);

  const clearConsumption = useCallback(() => {
    setProfiles(prev => ({
      ...prev,
      consumptionProfile: null,
      consumptionDemandMwh: null,
      consumptionStats: null,
      consumptionSeasonalFactors: null,
    }));
    setErrors(prev => ({ ...prev, consumption: null }));
  }, []);

  const clearGrid = useCallback(() => {
    setProfiles(prev => ({
      ...prev,
      gridProfile: null,
      gridPeakKw: null,
      gridDemandMwh: null,
      gridStats: null,
    }));
    setErrors(prev => ({ ...prev, grid: null }));
  }, []);

  return {
    profiles,
    errors,
    loading,
    handleSolarUpload,
    handleConsumptionUpload,
    handleGridUpload,
    clearSolar,
    clearConsumption,
    clearGrid,
    restoreProfiles,
  };
}
